import { useState, useEffect, createContext, useContext } from "react";
import {
  getCases,
  activateCase,
  deleteCase,
  saveCase,
  sendCompanySearch,
} from "../api/cases";
import { apiConfig } from "../api/AxiosClient";

const CompanyContext = createContext(null);

const LAST_COMPANY_TARGET_KEY = "ab:last-active-company-target";

const isCompanyActive = (company) => Boolean(company?.active);

const getCompanyKey = (company) => company?.target || company?.case || null;

const getStoredCompanyTarget = () => {
  try {
    return window.localStorage.getItem(LAST_COMPANY_TARGET_KEY);
  } catch {
    return null;
  }
};

const setStoredCompanyTarget = (target) => {
  try {
    if (target) {
      window.localStorage.setItem(LAST_COMPANY_TARGET_KEY, target);
    } else {
      window.localStorage.removeItem(LAST_COMPANY_TARGET_KEY);
    }
  } catch {
    // localStorage may be unavailable in private mode / SSR-ish contexts
  }
};

const getFallbackActiveCompany = (companies, excludedCompany = null) => {
  const excludedKey = getCompanyKey(excludedCompany);

  return (
    companies.find(
      (company) =>
        isCompanyActive(company) && getCompanyKey(company) !== excludedKey,
    ) ?? null
  );
};

const getInitialCurrentCompany = (companies) => {
  const activeCompanies = companies.filter(isCompanyActive);
  const savedTarget = getStoredCompanyTarget();

  if (savedTarget) {
    const savedCompany = activeCompanies.find(
      (company) => company.target === savedTarget,
    );

    if (savedCompany) return savedCompany;
  }

  return activeCompanies[0] ?? null;
};

export function CompanyProvider({ children }) {
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [companyKey, setCompanyKey] = useState(0);
  const [showAddCompany, setShowAddCompany] = useState(false);
  // False until the first getCases() resolves (or fails). Pages wait on this
  // so they never fire company-scoped requests before we know which Rx exist.
  const [companiesLoaded, setCompaniesLoaded] = useState(false);

  const selectCompany = (company) => {
    if (company && !isCompanyActive(company)) {
      console.warn(
        "[CompanyContext] Ignoring inactive company selection:",
        company,
      );
      return;
    }

    setCurrentCompany(company ?? null);
    apiConfig.companyName = company?.target ?? null;
    setStoredCompanyTarget(company?.target ?? null);
    setCompanyKey((k) => k + 1);
  };

  const refreshCompanies = async () => {
    const res = await getCases();
    const nextCompanies = res?.data ?? [];

    setCompanies(nextCompanies);

    setCurrentCompany((prev) => {
      const prevTarget = prev?.target;
      const stillValidCurrent = nextCompanies.find(
        (company) => company.target === prevTarget && isCompanyActive(company),
      );

      const nextCurrent =
        stillValidCurrent ?? getInitialCurrentCompany(nextCompanies);

      apiConfig.companyName = nextCurrent?.target ?? null;
      setStoredCompanyTarget(nextCurrent?.target ?? null);

      return nextCurrent;
    });

    return nextCompanies;
  };

  const toggleCompany = async (company, activate) => {
    await activateCase(company.target, activate);

    let nextCompaniesForSelection = [];

    setCompanies((prev) => {
      const next = prev.map((c) =>
        c.case === company.case ? { ...c, active: activate } : c,
      );

      nextCompaniesForSelection = next;
      return next;
    });

    if (!activate && currentCompany?.case === company.case) {
      const nextCurrent = getFallbackActiveCompany(
        nextCompaniesForSelection,
        company,
      );

      selectCompany(nextCurrent);
    }
  };

  const removeCompany = async (company) => {
    await deleteCase(company.target);

    let nextCompaniesForSelection = [];

    setCompanies((prev) => {
      const next = prev.filter((c) => c.case !== company.case);
      nextCompaniesForSelection = next;
      return next;
    });

    if (currentCompany?.case === company.case) {
      const nextCurrent = getFallbackActiveCompany(nextCompaniesForSelection);
      selectCompany(nextCurrent);
    }
  };

  const addCompany = async (result) => {
    console.log(result);
    await saveCase(result.name ?? result.case, result.domain ?? result.url);
    await refreshCompanies();
  };

  const searchCompanies = async (query) => {
    const res = await sendCompanySearch(query);
    return res?.data ?? [];
  };

  useEffect(() => {
    let cancelled = false;

    getCases()
      .then((res) => {
        if (cancelled) return;

        const nextCompanies = res?.data ?? [];
        const nextCurrent = getInitialCurrentCompany(nextCompanies);

        setCompanies(nextCompanies);
        setCurrentCompany(nextCurrent);
        apiConfig.companyName = nextCurrent?.target ?? null;
        setStoredCompanyTarget(nextCurrent?.target ?? null);
        setCompanyKey((k) => k + 1);
      })
      .catch((err) => {
        console.error("[CompanyContext] Failed to load cases:", err);
      })
      .finally(() => {
        if (!cancelled) setCompaniesLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <CompanyContext.Provider
      value={{
        companies,
        setCompanies,
        companiesLoaded,
        currentCompany,
        companyKey,
        selectCompany,
        refreshCompanies,
        toggleCompany,
        removeCompany,
        addCompany,
        searchCompanies,
        showAddCompany,
        setShowAddCompany,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanyContext() {
  const context = useContext(CompanyContext);

  if (!context) {
    throw new Error("useCompanyContext must be used within a CompanyProvider");
  }

  return context;
}
